'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { getLastCalculatorTab } from '@/lib/calculator-storage';
import { FEATURE_FLAGS } from '@/lib/env';

interface UseGlobalKeyboardShortcutsOptions {
  enabled?: boolean;
  onTabChange?: (tab: string) => void;
  lastCalculatorTab?: string;
  onReset?: () => void;
  onLocalReset?: () => void;
  onDebug?: () => void;
  onShowShortcuts?: () => void;
  onPrint?: () => void;
  onThemeToggle?: () => void;
  onShare?: () => void;
}

export function useGlobalKeyboardShortcuts({
  enabled = true,
  onTabChange,
  lastCalculatorTab = 'wizard',
  onReset,
  onLocalReset,
  onDebug,
  onShowShortcuts,
  onPrint,
  onThemeToggle,
  onShare
}: UseGlobalKeyboardShortcutsOptions = {}) {
  const router = useRouter();

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts when user is typing in input fields
      const activeElement = document.activeElement;
      const isInputField = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.getAttribute('contenteditable') === 'true'
      );

      if (isInputField) return;

      // Don't handle shortcuts when modifier keys are pressed (except for specific combinations)
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      // Handle keyboard shortcuts
      switch (e.key.toLowerCase()) {
        // Tab navigation shortcuts (work globally)
        case 's':
          e.preventDefault();
          if (onTabChange) {
            onTabChange('settings');
          } else {
            router.push('/calculator?tab=settings');
          }
          break;
        case 'l':
          e.preventDefault();
          if (onTabChange) {
            // Navigate to the last used calculator tab
            onTabChange(lastCalculatorTab);
          } else {
            // Get the last used tab from persistent storage and navigate there
            const persistentLastTab = getLastCalculatorTab();
            router.push(`/calculator?tab=${persistentLastTab}`);
          }
          break;
        case 'r':
          e.preventDefault();
          if (onLocalReset) {
            onLocalReset();
          }
          break;
        case 'h':
          e.preventDefault();
          if (onTabChange) {
            onTabChange('help');
          } else {
            router.push('/calculator?tab=help');
          }
          break;
        // New navigation shortcuts
        case 'd':
          e.preventDefault();
          if (onDebug) {
            onDebug();
          }
          break;
        case 'e':
          e.preventDefault();
          router.push('/examples');
          break;
        case 'a':
          if (!FEATURE_FLAGS.hideAboutPage) {
            e.preventDefault();
            router.push('/about');
          }
          break;
        case 'o':
          e.preventDefault();
          if (onReset) {
            onReset();
            if (onTabChange) {
              onTabChange(lastCalculatorTab);
            } else {
              const persistentLastTab = getLastCalculatorTab();
              router.push(`/calculator?tab=${persistentLastTab}`);
            }
          } else {
            const persistentLastTab = getLastCalculatorTab();
            router.push(`/calculator?tab=${persistentLastTab}`);
          }
          break;
        case 'p':
          e.preventDefault();
          if (onPrint) {
            onPrint();
          }
          break;
        case 'm':
          e.preventDefault();
          if (onThemeToggle) {
            onThemeToggle();
          }
          break;
        case 'x':
          e.preventDefault();
          if (onShare) {
            onShare();
          }
          break;
        // Help shortcuts
        case 'k':
        case '?':
          e.preventDefault();
          onShowShortcuts?.();
          break;
        default:
          // No action needed for other keys
          break;
      }
    };

    // Add event listener to document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, onTabChange, lastCalculatorTab, onReset, onLocalReset, onDebug, onShowShortcuts, onPrint, onThemeToggle, onShare, router]);
}